import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import SectionHeader from "@/components/base/SectionHeader";
import CTAPanel from "@/components/base/CTAPanel";
import RelatedContent from "@/components/feature/RelatedContent";

const serviceAreas = [
  {
    title: "Microsoft Azure Cloud Engineering",
    description: "Design, deployment, and administration of Microsoft Azure cloud environments, including Azure IaaS, PaaS, and hybrid cloud architectures for enterprise and government programs.",
    icon: "ri-cloud-line",
  },
  {
    title: "Cloud Migration & Modernization",
    description: "Planning and execution of workload migration to the cloud, including lift-and-shift, refactoring, and platform modernization to reduce cost and improve agility.",
    icon: "ri-arrow-left-right-line",
  },
  {
    title: "Virtual Machines & IaaS",
    description: "Azure Virtual Machine provisioning, sizing, patching, and lifecycle management to support enterprise infrastructure and application workloads.",
    icon: "ri-cpu-line",
  },
  {
    title: "Platform-as-a-Service (PaaS)",
    description: "Engineering on Azure PaaS services such as App Services, Functions, Containers, and Kubernetes (AKS) to reduce operational overhead and scale on demand.",
    icon: "ri-stack-line",
  },
  {
    title: "Cloud Storage & Backup",
    description: "Azure Storage design, data protection, backup, and disaster-recovery strategies to ensure availability, durability, and business continuity.",
    icon: "ri-hard-drive-3-line",
  },
  {
    title: "Cloud Networking & Security",
    description: "Azure virtual networks, load balancing, firewalls, identity (Entra ID), and security controls to protect cloud workloads and meet compliance requirements.",
    icon: "ri-shield-check-line",
  },
  {
    title: "Hybrid & Multi-Cloud",
    description: "Integration of on-premises infrastructure with Azure through hybrid connectivity, including Azure Arc and unified management approaches.",
    icon: "ri-node-tree",
  },
  {
    title: "Cloud Cost & Governance",
    description: "Azure governance, policy, tagging, and cost-optimization practices to maintain control, visibility, and efficiency across cloud environments.",
    icon: "ri-line-chart-line",
  },
  {
    title: "DevOps & CI/CD in the Cloud",
    description: "Cloud-native CI/CD pipelines, automated deployments, and Azure DevOps engineering that accelerate secure, repeatable software delivery.",
    icon: "ri-git-branch-line",
  },
];

export default function CloudServices() {
  return (
    <Layout>
      <PageHero
        title="Cloud Services"
        subtitle="Proven Core Capability"
        description="Cloud engineering and migration services built on Microsoft Azure, covering IaaS, PaaS, hybrid cloud, security, and DevOps for enterprise and government programs."
        size="md"
      />

      <section className="section-padding bg-white">
        <div className="container-page">
          <SectionHeader
            title="Service Areas"
            description="Cloud services delivered through Mitaja's enterprise infrastructure and platform engineering practice."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceAreas.map((area) => (
              <div key={area.title} className="card-base p-6">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                  <i className={`${area.icon} text-primary-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-900 mb-2">
                  {area.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {area.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-14 bg-background-100 rounded-lg p-6 border border-background-200/80">
            <h3 className="text-lg font-bold text-foreground-900 mb-4">
              Capability Overview
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Mitaja's cloud services practice is anchored on Microsoft Azure, delivering cloud engineering, migration, and modernization for enterprise and government environments. Services span Azure IaaS and PaaS, hybrid cloud connectivity, storage and backup, cloud security and identity, cost governance, and cloud-native DevOps. These capabilities complement Mitaja's broader infrastructure, application, and data platform services.
            </p>
          </div>
        </div>
      </section>

      <RelatedContent
        subtitle="Related Information"
        title="Cloud Experience & Delivery Details"
        description="See how Mitaja's Azure cloud engineering connects to its broader enterprise technology experience."
        links={[
          {
            label: "U.S. Government Experience",
            description: "Documented federal, state and local government technology delivery through subcontracting relationships.",
            href: "/experience/usa-government",
            icon: "ri-government-line",
          },
          {
            label: "Capability Statement",
            description: "Review Mitaja's capability statement for procurement, teaming, and qualification review.",
            href: "/resources/capability-statement",
            icon: "ri-file-list-line",
          },
          {
            label: "Contact Mitaja",
            description: "Discuss Azure cloud engineering, migration, or teaming requirements with our team.",
            href: "/contact",
            icon: "ri-mail-line",
          },
        ]}
      />

      <CTAPanel
        title="Planning a Cloud Migration?"
        description="Mitaja provides qualified Azure cloud engineers and migration specialists for enterprise and government programs."
        secondaryAction={{ label: "Contact Us", href: "/contact" }}
        variant="dark"
      />
    </Layout>
  );
}